import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';
import { useProfile } from '../../context/ProfileContext';
import { getAuth } from "firebase/auth";
import { getFirestore, collection, query, where, orderBy, getDocs, onSnapshot } from "firebase/firestore";
import { useMusic } from '../../context/MusicContext';
import toast from "react-hot-toast";

// Import all necessary child components
import CampaignBuilderModal from "../../components/artistDashboard/labelServices/CampaignBuilderModal";
import DropdownMenu from "../../components/profile/DropdownMenu";
import EditProfileModal from "../../components/profile/EditProfileModal";
import SwitchAccountsModal from "../../components/profile/SwitchAccountsModal";
import ProfileHeader from "../../components/profile/ProfileHeader";
import CreatePlaylistModal from '../../components/music/CreatePlaylistModal';
import ArtistProfile from "./sections/ArtistProfile";
import RegularUserProfile from "./sections/RegularUserProfile";
import CreateAlbumModal from '../../components/albums/CreateAlbumModal';

const UserProfilePage = () => {
  // ✅ 1. Get ALL necessary data directly from the context.
  const { activeProfile, user, userProfile, linkedArtistAccounts, switchProfile, isDarkMode, setActiveProfile, setUserProfile } = useProfile()
  const { setQueueAndPlay } = useMusic();
  const db = getFirestore();
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [showSwitchGuideModal, setShowSwitchGuideModal] = useState(false);

  // State for modals and playlists remains here
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [postToEdit, setPostToEdit] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isUploadSongOpen, setIsUploadSongOpen] = useState(false);
  const [isCreateAlbumOpen, setIsCreateAlbumOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [isPostsLoading, setIsPostsLoading] = useState(false);

  const activeTab = searchParams.get('tab') || 'discography';

  const fetchPosts = useCallback(async () => {
      if (activeProfile?.type !== 'artist' || !activeProfile?.id) {
          setPosts([]);
          return;
      }

      if (posts.length === 0) {
          setIsPostsLoading(true);
      }

      try {
          const postsRef = collection(db, "artistPosts");
          const q = query(
              postsRef,
              where("artistId", "==", activeProfile.id),
              orderBy("createdAt", "desc")
          );
          const querySnapshot = await getDocs(q);
          let fetchedPosts = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          const visiblePosts = fetchedPosts.filter(post => !post.deletedAt);
          setPosts(visiblePosts);
      } catch (error) {
          console.error("Error fetching posts:", error);
      } finally {
          setIsPostsLoading(false);
      }
  }, [activeProfile, db, posts.length]);

  //Artist Profile Set-up on-boarding Wizard Pop Up
  useEffect(() => {
    if (location.state?.triggerArtistOverlay) {
      setShowArtistModal(true);
      // Clear location state after reading
      window.history.replaceState({}, document.title);
    }
    if (location.state?.triggerSwitchAccountOverlay) {
      setShowSwitchGuideModal(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
      if (activeProfile?.type === 'artist' && activeTab === 'press') {
          fetchPosts();
      }
      // Note: The logic for fetching songs remains inside ArtistProfilePage for now
  }, [activeProfile, activeTab, fetchPosts]);
  
  const handleEditPost = (post) => {
      setPostToEdit(post);
      setIsCampaignModalOpen(true);
  };

  const handleClosePostModal = () => {
      setIsCampaignModalOpen(false);
      setPostToEdit(null);
      fetchPosts(); // Refresh the posts when the modal closes
  };

  const handleCreateClick = useCallback((type) => {
    if (type === 'song') {
      navigate('/upload-music', { state: { artistProfile: activeProfile } });
    } else if (type === 'album') {
      setIsCreateAlbumOpen(true);
    } else if (type === 'announcement') {
      setIsCampaignModalOpen(true);
    }
  }, [navigate, activeProfile]);

  const handleCreatePlaylistClick = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  // ✅ 2. The complex data-fetching useEffect has been REMOVED.
  // This component now trusts the ProfileContext to provide the correct activeProfile.

  // This effect to fetch playlists based on the activeProfile is still correct.
  useEffect(() => {
    if (!activeProfile?.id) {
      setPlaylists([]);
      return;
    }
    const db = getFirestore();
    const q = query(collection(db, 'playlists'), where('ownerId', '==', activeProfile.id));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPlaylists(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [activeProfile]);
  
  // This logic for the switch accounts modal is now correct.
  const filteredAccounts = useMemo(() => {
    // Failsafe in case the context data isn't ready yet
    if (!activeProfile || !userProfile || !linkedArtistAccounts) return [];

    // ✅ 1. Create a complete list using the full userProfile object from context.
    // We add the 'id' and 'type' here to ensure the object is consistent.
    const allOwnedProfiles = [
      { ...userProfile, id: userProfile.id || user.uid, type: 'user' },
      ...linkedArtistAccounts
    ];

    // ✅ 2. The filter logic correctly removes the currently active profile.
    return allOwnedProfiles.filter(acc => acc.id !== activeProfile.id || acc.type !== activeProfile.type);

  }, [activeProfile, userProfile, linkedArtistAccounts]);

  const handleMenuOptionClick = async (option) => {
    switch (option) {
      case "editProfile": setIsEditModalOpen(true); break;
      case "switchAccounts": setIsSwitchModalOpen(true); break;
      case "createArtist": navigate("/artist-account-form"); break;
      case "signOut": 
      try {
        await auth.signOut();
        // Clear local storage to prevent ghost profile state data on re-login
        localStorage.removeItem("activeProfile");
        if (setActiveProfile) setActiveProfile(null);
        if (setUserProfile) setUserProfile(null);
        
        toast.success("Signed out successfully");
        navigate("/"); // Redirect to landing or login route
      } catch (error) {
        console.error("Error signing out:", error);
        toast.error("Failed to sign out.");
      }
      break;
      default: console.log(`Menu option "${option}" clicked.`);
    }
  };

  const handleSelectAccount = (account) => {
    switchProfile(account);
    setIsSwitchModalOpen(false);
    setSearchParams({}, { replace: true });
  };
  
  if (!activeProfile) {
    return <p className="text-center mt-8">Loading profile...</p>;
  }

  

  return (
    /* ✅ FIX: Removed bg-gray-50. Using min-h-screen with flex-col to ensure background stretches. */
    <div className="relative min-h-screen flex flex-col bg-white dark:bg-brand-black transition-colors duration-500">
      
      {/* 🟢 TOP ACTION AREA */}
      <div className="absolute top-0 right-0 z-50">
         <DropdownMenu onOptionClick={handleMenuOptionClick} activeProfile={activeProfile} />
      </div>

      <div className="flex-grow p-4 pb-32">
        <ProfileHeader
            profile={activeProfile}
            isOwnProfile={true}
            onCreatePlaylistClick={() => setIsCreateModalOpen(true)} 
            onCreateClick={handleCreateClick}
        />
        
        {/* ✅ CONTENT SWITCHER */}
        <div className="mt-4">
            {activeProfile.type === "artist" ? (
              <ArtistProfile 
                artistProfile={activeProfile}
                posts={posts}
                isPostsLoading={isPostsLoading}
                fetchPosts={fetchPosts}
                setQueueAndPlay={setQueueAndPlay}
                onEditPost={(p) => { setPostToEdit(p); setIsCampaignModalOpen(true); }}
              />
            ) : (
              <RegularUserProfile 
                userProfile={activeProfile} 
                playlists={playlists}
                onPlaylistClick={(p) => navigate(`/playlist/${p.id}`)}
              />
            )}
        </div>
      </div>

      {/* ✅ MODALS AREA */}
      <EditProfileModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} />
      <SwitchAccountsModal
          isOpen={isSwitchModalOpen}
          onClose={() => setIsSwitchModalOpen(false)}
          accounts={[
            { ...userProfile, type: 'user', id: user?.uid }, 
            ...linkedArtistAccounts
          ]}
          onSelectAccount={(acc) => { switchProfile(acc); setIsSwitchModalOpen(false); }}
      />
      <CreatePlaylistModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} source="user_profile_page" />
      <CampaignBuilderModal
          isOpen={isCampaignModalOpen}
          onClose={() => { setIsCampaignModalOpen(false); fetchPosts(); }} 
          artistId={activeProfile?.id}
          artistProfile={activeProfile}
          postToEdit={postToEdit}
      />
      <CreateAlbumModal 
          isOpen={isCreateAlbumOpen} 
          onClose={() => {
              setIsCreateAlbumOpen(false);
          }}
      />

      {/* 🚀 NEW: CUSTOM TRANSLUCENT TOP-RIGHT NOTIFICATION MODAL */}
      <AnimatePresence>
        {showArtistModal && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed top-6 right-6 z-60 w-full max-w-sm p-5 bg-slate-900/85 backdrop-blur-md border-2 border-[#ff00cc] rounded-2xl shadow-[0_0_20px_rgba(255,0,204,0.15)] text-white"
          >
            <div className="space-y-4">
              {/* Modal Content Headers */}
              <div className="space-y-1">
                <h4 className="text-sm font-black uppercase tracking-wider text-[#ff00cc]">
                  To create an Artist Profile:
                </h4>
                <p className="text-base font-bold text-white/90 leading-snug">
                  Select <span className="text-[#ff00cc]">"Accounts & Profiles"</span> in the <span className="text-[#ff00cc]">"Settings"</span> menu and choose <span className="text-[#ff00cc]">"Create New Artist Account"</span>
                </p>
              </div>

              {/* Dismissal Control Button */}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowArtistModal(false)}
                  className="px-4 py-1.5 text-xs font-black uppercase tracking-widest bg-white text-slate-900 hover:bg-[#ff00cc] hover:text-white rounded-lg transition-colors duration-200"
                >
                  Got it
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🌟 POST-ARTIST CREATION SWITCH ACCOUNTS OVERLAY */}
      <AnimatePresence>
        {showSwitchGuideModal && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed top-6 right-6 z-60 w-full max-w-sm p-5 bg-slate-900/85 backdrop-blur-md border-2 border-[#ff00cc] rounded-2xl shadow-[0_0_20px_rgba(255,0,204,0.15)] text-white"
          >
            <div className="space-y-4">
              <div className="space-y-1">
                <h4 className="text-sm font-black uppercase tracking-wider text-[#ff00cc]">
                  Account Switching:
                </h4>
                <p className="text-sm font-bold text-white/90 leading-snug">
                  Swap between your Artist and Listener Profile using <span className="text-[#ff00cc]">"Accounts & Profiles"</span> in the <span className="text-[#ff00cc]">"Settings"</span> menu.
                </p>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowSwitchGuideModal(false)}
                  className="px-4 py-1.5 text-xs font-black uppercase tracking-widest bg-white text-slate-900 hover:bg-[#ff00cc] hover:text-white rounded-lg transition-colors duration-200"
                >
                  Got it
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserProfilePage;